import { IFieldArray } from "AngularFormly";
import { Record } from "../../../../../../records/server/crud";
import { StateService } from "@uirouter/angularjs";
import {
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
} from "../../../../../../records/forms/formView";
import { ServiceResponse } from "../../../../../../records/server/query";
import { IOnInit } from "angular";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../../../../records/forms/formly/fieldConfigurationHelpers";
import fieldHelpers from "../../../../../../ui-formly/fieldHelpers";

import * as lookupNames from "../../../../configs/lookup";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../../../records/server/serverUtility";
import { get, set } from "lodash";
import { LookupManagementService } from "../../../../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName } from "../../../../../shared/lookups";

export class MachineTypeIdEditComponentController implements IOnInit {
  static $inject = [
    "$state",
    FormViewServiceName,
    FieldConfigurationHelperServiceName,
    ServerUtilityName,
    vsLookupManagementServiceName,
  ];

  private fields!: IFieldArray;
  readonly typeId!: Record;

  constructor(
    private readonly $state: StateService,
    private readonly formViewService: FormViewService,
    private readonly fieldConfigurationHelperService: FieldConfigurationHelperService,
    private readonly serverUtility: ServerUtility,
    private readonly lookupManagementService: LookupManagementService
  ) {}

  getFields(): IFieldArray {
    const that = this;
    let progressiveGames: any[] =
      (<any>that.typeId.ProgressiveDefinition)?.Games ?? [];

    return [
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleStringField({
            key: "Name",
            label: "Type ID",
            required: true,
          }),
        ],
        4
      ),
      {
        className: "row",
        fieldGroup: [
          fieldHelpers.simpleStringField({
            key: "Description",
            label: "Description",
            required: true,
            className: "col-xs-6",
          }),
          that.fieldConfigurationHelperService.selectAsyncField({
            key: "ProgressiveDefinitionId",
            label: "Progressive",
            useWrappedQuery: true,
            queryUrl: "/api/machines/progressives/ProgressiveDefinition",
            getDefaultItem: (model: any) => {
              return {
                Id: model.ProgressiveDefinition?.Id,
                Name: model.ProgressiveDefinition?.Name,
              };
            },
            onSelect: (progressive: any) => {
              (<any[]>that.typeId.GameConfigs)?.forEach(
                (gc: any) => (gc.ProgressiveDefinitionGameId = undefined)
              );
              if (!progressive) {
                progressiveGames = [];
                return;
              }
              that.serverUtility
                .getById<ServiceResponse<any>>({
                  url: "/api/machines/progressives/ProgressiveDefinition",
                  id: progressive.Id,
                })
                .then((result) => {
                  progressiveGames = result.Result?.Games ?? [];
                });
            },
            className: "col-xs-3",
          }),
          fieldHelpers.simpleStringField({
            key: "SeriesFamily",
            label: "Series/Family",
            className: "col-xs-3",
          }),
        ],
      },
      ...fieldHelpers.columnLayout(
        [
          this.fieldConfigurationHelperService.lookupField(
            this.lookupManagementService,
            {
              key: "MachineGameTypeId",
              objectName: lookupNames.gameTypeName,
              required: true,
              machineField: true,
              showAddButton: true,
              showAddButtonPermission: "machineLookupModify",
            }
          ),
          fieldHelpers.simpleBoolField({
            key: "FreePlayEnabled",
            label: "Free Play Enabled",
            className: "col-xs-3",
            defaultValue: false,
          }),
          fieldHelpers.simpleBoolField({
            key: "FirstToOregon",
            label: "First to Oregon",
            defaultValue: false,
          }),
        ],
        4
      ),
      that.fieldConfigurationHelperService.repeaterField({
        key: "GameConfigs",
        label: "Game Configs",
        allowAdd: true,
        allowAddBottom: true,
        displayNameSingular: "Game Configuration",
        displayNamePlural: "Game Configurations",
        repeaterFields: [
          ...fieldHelpers.columnLayout(
            [
              that.fieldConfigurationHelperService.lookupField(
                this.lookupManagementService,
                {
                  key: "MachineThemeId",
                  objectName: lookupNames.themeName,
                  label: "Theme",
                  showAddButton: true,
                  showAddButtonPermission: "machineLookupModify",
                }
              ),
              that.fieldConfigurationHelperService.lookupField(
                this.lookupManagementService,
                {
                  key: "MachineDenominationId",
                  objectName: lookupNames.denominationName,
                  label: "Denomination",
                  showAddButton: true,
                  showAddButtonPermission: "machineLookupModify",
                }
              ),
              fieldHelpers.simpleCurrencyField({
                key: "MinBet",
                label: "Min Bet",
                min: 0,
              }),
              fieldHelpers.simpleCurrencyField({
                key: "MaxBet",
                label: "MaxBet",
                min: 0,
              }),
              fieldHelpers.simpleCurrencyField({
                key: "C2C",
                label: "C2C $",
                min: 0,
                step: 0.01,
              }),
              fieldHelpers.simpleNumberField({
                key: "Payline",
                label: "P/L",
                min: 0,
              }),
              fieldHelpers.simplePercentageField({
                key: "GamePar",
                label: "PAR",
              }),
              fieldHelpers.simplePercentageField({
                key: "ParSheetPar",
                label: "Par Sheet Par",
              }),
              {
                key: "ProgressiveDefinitionGameId",
                name: "ProgressiveDefinitionGameId",
                type: "select",
                templateOptions: {
                  label: "Progressive Game",
                  labelProp: "Description",
                  valueProp: "Id",
                },
                watcher: [
                  {
                    expression: () => progressiveGames,
                    listener: (field, newValue) => {
                      field.templateOptions!.options = newValue;
                    },
                  },
                  {
                    expression: (field, scope) => get(scope.model, field.key!),
                    listener: (field, newValue, oldValue, scope) => {
                      const progressiveDefinitionGame = progressiveGames.find(
                        (g) => g.Id === newValue
                      );
                      set(
                        scope.model!,
                        "ProgressiveDefinitionGame",
                        progressiveDefinitionGame
                      );
                    },
                  },
                ],
              },
              fieldHelpers.simpleStringField({
                key: "Variation",
              }),
              fieldHelpers.simpleStringField({
                key: "PaytableId",
                label: "Paytable ID",
              }),
              // fieldHelpers.simplePercentageField({
              //   key: "RtpPercentage",
              //   label: "RTP%",
              //   min: 0,
              // }),
              {
                key: "copyDelete",
                type: "recordRepeaterCopyDelete",
                templateOptions: {
                  label: " ",
                  allowMove: true,
                },
              },
            ],
            4
          ),
        ],
        showHeader: true,
      }),
    ];
  }

  submit(formViewController: FormViewController) {
    const isNewRecord = this.isNewRecord();
    const method = isNewRecord ? "post" : "put";
    const url =
      "/api/machines/MachineTypeId" + (isNewRecord ? "" : `/${this.typeId.Id}`);
    return this.formViewService
      .submitForm<ServiceResponse<Record>>({
        formObject: this.typeId,
        formViewController,
        method,
        url,
      })
      .then((result) =>
        this.$state.go("main.machines.typeId.info.redirect", {
          typeId: result.Result.Id,
        })
      );
  }

  $onInit(): void {
    this.fields = this.getFields();
  }

  cancel() {
    return this.$state.go("^.redirect");
  }

  private isNewRecord() {
    return !this.typeId.Id;
  }
}
